import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ChevronRight, Droplets, History } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import {
  factoryService,
  type FactoryBatch,
  type FactoryIpttqRecord,
  type FactoryReleaseRequest,
} from '../../services/factoryService';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';

const releaseLabel: Record<string, string> = {
  draft: '草稿',
  submitted: '已提交',
  accepted: '已受理',
  recalled: '已召回',
  rejected: '已拒绝',
};

type BatchDetailState = {
  loading: boolean;
  error: string | null;
  records: FactoryIpttqRecord[];
};

function receiptHint(raw: string | undefined): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (text.includes('owner_stopped_release')) return 'Owner 停止释放';
  if (text.includes('functor_path_unwired')) return 'Functor 路径未接通';
  if (text.includes('deferred:')) return `延迟：${text.replace(/^deferred:/, '')}`;
  return text;
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dayLabel(key: string): string {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function groupHistoryByDay(items: FactoryReleaseRequest[]) {
  const map = new Map<string, FactoryReleaseRequest[]>();
  for (const r of items) {
    const key = dayKey(r.createdAt);
    const list = map.get(key) || [];
    list.push(r);
    map.set(key, list);
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

function productTitle(rec: FactoryIpttqRecord): string {
  return String(
    rec.skill?.mainTitle
    || rec.factoryMeta?.warehouseTtqaTitle
    ||     rec.id
    || '成品智能体',
  ).trim() || '成品智能体';
}

export function FactoryReleasePage() {
  const { refreshKey = 0 } = useOutletContext<FactoryDeskOutletContext>() ?? { refreshKey: 0 };
  const { activeOrderId } = useFactoryOrderScope();
  const [eligible, setEligible] = useState<FactoryBatch[]>([]);
  const [requests, setRequests] = useState<FactoryReleaseRequest[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [expandedDetailId, setExpandedDetailId] = useState<string | null>(null);
  const [detailByBatch, setDetailByBatch] = useState<Record<string, BatchDetailState>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [goods, list] = await Promise.all([
      factoryService.listGoods(),
      factoryService.listReleaseRequests(),
    ]);
    // Pipeline ready counts even when warehouse_status was never stamped (legacy rows).
    const nextEligible = goods.filter((g) => {
      if (g.warehouseStatus === 'scrapped') return false;
      if (g.warehouseStatus === 'offline' || g.warehouseStatus === 'ready') return true;
      return g.status === 'ready';
    });
    setEligible(nextEligible);
    setRequests(list);
    setSelectedBatchId((prev) => {
      if (prev && nextEligible.some((b) => b.id === prev)) return prev;
      const releasing = nextEligible.find((b) => b.releaseAuthorization === 'authorized');
      return releasing?.id || nextEligible[0]?.id || null;
    });
  }, []);

  const loadBatchDetail = useCallback(async (batchId: string) => {
    setDetailByBatch((prev) => ({
      ...prev,
      [batchId]: {
        loading: true,
        error: null,
        records: prev[batchId]?.records || [],
      },
    }));
    try {
      const res = await factoryService.getIpttq(batchId);
      const records = Array.isArray(res.ipttq?.records) ? res.ipttq!.records : [];
      setDetailByBatch((prev) => ({
        ...prev,
        [batchId]: { loading: false, error: null, records },
      }));
    } catch (err) {
      setDetailByBatch((prev) => ({
        ...prev,
        [batchId]: {
          loading: false,
          error: err instanceof Error ? err.message : String(err),
          records: prev[batchId]?.records || [],
        },
      }));
    }
  }, []);

  const toggleBatchDetail = useCallback((batchId: string) => {
    setSelectedBatchId(batchId);
    setExpandedDetailId((prev) => {
      const next = prev === batchId ? null : batchId;
      if (next) void loadBatchDetail(next);
      return next;
    });
  }, [loadBatchDetail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        if (refreshKey > 0) setLoading(true);
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, refreshKey]);

  async function submit(batchId: string) {
    setBusyId(batchId);
    setError(null);
    try {
      await factoryService.submitRelease(batchId);
      await reload();
      setSelectedBatchId(batchId);
      setExpandedHistoryId(batchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function recall(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await factoryService.recallRelease(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  const scopedEligible = useMemo(
    () => (activeOrderId ? eligible.filter((g) => g.orderId === activeOrderId) : []),
    [activeOrderId, eligible],
  );
  const scopedRequests = useMemo(
    () => requests.filter((r) => scopedEligible.some((g) => g.id === r.batchId)),
    [requests, scopedEligible],
  );

  useEffect(() => {
    if (selectedBatchId && scopedEligible.some((b) => b.id === selectedBatchId)) return;
    const releasing = scopedEligible.find((b) => b.releaseAuthorization === 'authorized');
    setSelectedBatchId(releasing?.id || scopedEligible[0]?.id || null);
  }, [scopedEligible, selectedBatchId]);

  const historyByBatch = useMemo(() => {
    const map = new Map<string, FactoryReleaseRequest[]>();
    for (const r of scopedRequests) {
      const list = map.get(r.batchId) || [];
      list.push(r);
      map.set(r.batchId, list);
    }
    for (const [batchId, list] of map) {
      list.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      map.set(batchId, list);
    }
    return map;
  }, [scopedRequests]);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-ink">释放</h1>
        <p className="mt-1 text-sm text-muted">
          对本订单已下线或仓内就绪的成品智能体发起释放申请；需 Owner 授权后才会滴灌。成品智能体是后续释放知识颗粒到复习的来源。切换订单请回「订单」。
        </p>
      </header>
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-ink">可释放成品智能体</h2>
        {loading ? <p className="mt-2 text-sm text-muted">加载中…</p> : null}
        {!loading && scopedEligible.length === 0 ? (
          <p className="mt-2 text-sm text-muted">
            {activeOrderId
              ? '当前订单暂无可释放成品智能体。批次「就绪」后会出现在此。'
              : '请先在「订单」选择当前订单。'}
          </p>
        ) : null}
        <ul className="mt-3 space-y-2">
          {scopedEligible.map((b) => {
            const releasing = b.releaseAuthorization === 'authorized';
            const selected = selectedBatchId === b.id;
            const batchHistory = historyByBatch.get(b.id) || [];
            const historyCount = batchHistory.length;
            const historyOpen = expandedHistoryId === b.id;
            const detailOpen = expandedDetailId === b.id;
            const detail = detailByBatch[b.id];
            const calendarDays = historyOpen ? groupHistoryByDay(batchHistory) : [];
            return (
              <li
                key={b.id}
                className={`rounded-xl border px-3 py-2 transition ${
                  selected
                    ? 'border-primary/50 bg-primary/10'
                    : 'border-border hover:bg-white/[0.03]'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    aria-expanded={detailOpen}
                    onClick={() => toggleBatchDetail(b.id)}
                  >
                    <ChevronRight
                      size={16}
                      strokeWidth={2.2}
                      className={`mt-0.5 shrink-0 text-muted transition-transform ${
                        detailOpen ? 'rotate-90 text-primary' : ''
                      }`}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink">
                        {b.title || b.id}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-muted">
                        {b.status === 'ready' ? '就绪' : b.status}
                        {b.warehouseStatus && b.warehouseStatus !== 'none'
                          ? ` · 仓 ${b.warehouseStatus}`
                          : ''}
                        {b.releaseAuthorization === 'awaiting_owner_auth'
                          ? ' · 已申请 · 等 Owner'
                          : releasing
                            ? ' · 已授权释放'
                            : ''}
                        {historyCount ? ` · 历史 ${historyCount}` : ''}
                      </span>
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      title={historyOpen ? '收起历史' : '展开提交与召回历史'}
                      aria-label={historyOpen ? '收起历史' : '展开提交与召回历史'}
                      aria-expanded={historyOpen}
                      onClick={() => {
                        setSelectedBatchId(b.id);
                        setExpandedHistoryId((prev) => (prev === b.id ? null : b.id));
                      }}
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-lg transition ${
                        historyOpen
                          ? 'bg-primary/20 text-primary ring-1 ring-primary/40'
                          : 'text-muted hover:bg-white/[0.06] hover:text-ink'
                      }`}
                    >
                      <History size={14} strokeWidth={2.2} aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={busyId === b.id || releasing}
                      onClick={() => {
                        setSelectedBatchId(b.id);
                        if (!releasing) void submit(b.id);
                      }}
                      className={`inline-flex min-w-[9.5rem] items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-90 ${
                        releasing
                          ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/25 ring-1 ring-emerald-300/50'
                          : 'bg-primary text-white'
                      }`}
                    >
                      {releasing ? (
                        <>
                          <Droplets
                            size={14}
                            strokeWidth={2.2}
                            className="factory-droplets-drip shrink-0 text-white"
                            aria-hidden
                          />
                          已在释放
                        </>
                      ) : busyId === b.id ? (
                        '提交中…'
                      ) : (
                        '提交释放'
                      )}
                    </button>
                  </div>
                </div>

                {detailOpen ? (
                  <div className="mt-2 space-y-3 border-t border-border/70 pt-2 pl-6">
                    {detail?.loading ? (
                      <p className="text-xs text-muted">加载成品智能体与诱导题…</p>
                    ) : null}
                    {detail?.error ? (
                      <p className="text-xs text-danger">{detail.error}</p>
                    ) : null}
                    {!detail?.loading && !detail?.error && (detail?.records.length ?? 0) === 0 ? (
                      <p className="text-xs text-muted">
                        尚无品题浓缩成品智能体 / 诱导题草稿。请先在任务结果页完成 IPTTQ。
                      </p>
                    ) : null}
                    {!detail?.loading && detail?.records.length ? (
                      <ul className="space-y-3">
                        {detail.records.map((rec, recIndex) => {
                          const drafts = Array.isArray(rec.bankParticleDrafts)
                            ? rec.bankParticleDrafts
                            : [];
                          return (
                            <li
                              key={rec.id || `rec-${recIndex}`}
                              className="rounded-lg border border-border/70 bg-canvas/20 px-3 py-2"
                            >
                              <div className="text-sm font-semibold text-ink">
                                <span className="mr-1.5 tabular-nums text-muted">
                                  {recIndex + 1}.
                                </span>
                                <MathTextBlock
                                  inline
                                  text={productTitle(rec)}
                                  className="text-sm font-semibold"
                                />
                              </div>
                              {rec.skill?.thinkingLineage ? (
                                <div className="mt-1.5 text-xs text-muted">
                                  <span className="font-medium text-ink/80">思考脉络 · </span>
                                  <MathTextBlock
                                    inline
                                    text={rec.skill.thinkingLineage}
                                    className="text-xs text-muted"
                                  />
                                </div>
                              ) : null}
                              <div className="mt-2">
                                <div className="text-[11px] font-medium text-muted">
                                  诱导题（{drafts.length}）
                                </div>
                                {drafts.length === 0 ? (
                                  <p className="mt-1 text-xs text-muted">尚无诱导题草稿</p>
                                ) : (
                                  <ol className="mt-1 space-y-1.5">
                                    {drafts.map((draft, draftIndex) => (
                                      <li
                                        key={draft.id || `draft-${draftIndex}`}
                                        className="flex gap-2 text-sm text-ink"
                                      >
                                        <span className="w-5 shrink-0 tabular-nums text-muted">
                                          {draftIndex + 1}.
                                        </span>
                                        <span className="min-w-0 flex-1">
                                          <MathTextBlock
                                            inline
                                            text={
                                              draft.title
                                              || draft.metadata?.displayTitle
                                              || draft.questions?.main
                                              || `诱导题 ${draftIndex + 1}`
                                            }
                                            className="text-sm"
                                          />
                                        </span>
                                      </li>
                                    ))}
                                  </ol>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                ) : null}

                {historyOpen ? (
                  <div className="mt-2 space-y-4 border-t border-border/70 pt-2">
                    {batchHistory.length === 0 ? (
                      <p className="text-xs text-muted">尚无释放申请记录。提交后会按日汇总在此。</p>
                    ) : (
                      calendarDays.map(([day, items]) => (
                        <div key={day}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-xs font-semibold text-ink">{dayLabel(day)}</span>
                            <span className="h-px flex-1 bg-border/60" />
                            <span className="text-[11px] tabular-nums text-muted">{items.length}</span>
                          </div>
                          <ol className="relative space-y-2 border-l border-border/70 pl-3">
                            {items.map((r) => {
                              const hint = receiptHint(r.functorReceipt);
                              const canRecall = r.status === 'submitted' || r.status === 'accepted';
                              return (
                                <li
                                  key={r.id}
                                  className="relative rounded-xl border border-border/80 bg-canvas/20 px-3 py-2"
                                >
                                  <span className="absolute -left-[17px] top-3 h-2.5 w-2.5 rounded-full border border-border bg-primary" />
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2 text-sm">
                                        <span className="font-medium text-ink">
                                          {releaseLabel[r.status] || r.status}
                                        </span>
                                        <span className="text-[11px] tabular-nums text-muted">
                                          {timeLabel(r.createdAt)}
                                        </span>
                                      </div>
                                      {hint ? (
                                        <div className="mt-1 text-xs text-muted break-all">{hint}</div>
                                      ) : null}
                                      <div className="mt-1 text-[11px] text-muted">
                                        申请 {r.id.slice(0, 8)}…
                                        {r.updatedAt && r.updatedAt !== r.createdAt
                                          ? ` · 更新 ${new Date(r.updatedAt).toLocaleString()}`
                                          : ''}
                                      </div>
                                    </div>
                                    {canRecall ? (
                                      <button
                                        type="button"
                                        disabled={busyId === r.id}
                                        onClick={() => void recall(r.id)}
                                        className="rounded-lg border border-border px-3 py-1.5 text-xs disabled:opacity-50"
                                      >
                                        {busyId === r.id ? '召回中…' : '召回'}
                                      </button>
                                    ) : null}
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      ))
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
